import { useEffect, useRef, useState } from "react";
import "@/App.css";

// Catalog destinations — update when user provides actual URLs.
const PREMIUM_ONE_STOP_URL = "https://underground-mix.disco.ac/cat/1530971836";
const EASY_CLEAR_URL = "https://the-bus-music.disco.ac/cat/1314266561";

const NAV_ITEMS = [
  { label: "About", href: "#about" },
  { label: "Listen", href: "#listen" },
  { label: "Reel", href: "#reel" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "Live Chat", href: "#live-chat" },
  { label: "Contact", href: "#contact" },
];

const PLAYLIST_PREMIUM_ONE_STOP_SRC =
  "https://underground-mix.disco.ac/e/p/29325047?download=false&s=9OBPJCYBkYJ5mtCDhHN4crQfHg8%3ASroNfgNa&artwork=true&color=%234E98FF&theme=white";

// Easy Clear playlist src
const PLAYLIST_EASY_CLEAR_SRC =
  "https://the-bus-music.disco.ac/e/p/29325051?download=false&s=fxPNBAiONpzBFRsteC4L9mXYCiY%3AAD9N4i2k&artwork=true&color=%234E98FF&theme=white";

const REEL_URL =
  "https://customer-assets.emergentagent.com/job_disco-catalog/artifacts/ck1mffgk_The%20Bus%20%26%20UM%20Reel%20%282%29.mp4";

const LOGO_THE_BUS =
  "https://customer-assets.emergentagent.com/job_disco-catalog/artifacts/b3jn35qc_white%20transparent%20bus%20logo.png";

const LOGO_UNDERGROUND_MIX =
  "https://customer-assets.emergentagent.com/job_disco-catalog/artifacts/jaabhsh5_PNG-03.png";

/* -------------------------------------------------------------------------- */
/* Atmosphere                                                                  */
/* -------------------------------------------------------------------------- */
function BackgroundNoise() {
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.18] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.95 0 0 0 0 0.92 0 0 0 0 0.85 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed left-1/2 top-[40vh] z-[1] h-[52vmin] w-[52vmin] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-[0.18] blur-[120px]"
        style={{ background: "#8f2b1c" }}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Navigation                                                                  */
/* -------------------------------------------------------------------------- */
function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-nav"
      className="fixed left-0 right-0 top-0 z-30 transition-all duration-500"
      style={{
        background: scrolled ? "rgba(5,5,5,0.78)" : "transparent",
        backdropFilter: scrolled ? "blur(10px)" : "none",
        borderBottom: scrolled ? "1px solid #2a2a26" : "1px solid transparent",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-10">
        {/* Brand */}
        <a
          href="#top"
          onClick={(e) => handleClick(e, "#top")}
          data-testid="brand-mark"
          className="brand-mark group flex items-center gap-3 sm:gap-4"
          style={{ textDecoration: "none" }}
        >
          <img
            src={LOGO_THE_BUS}
            alt=""
            aria-hidden="true"
            className="brand-logo h-14 w-14 sm:h-16 sm:w-16 object-contain"
          />
          <img
            src={LOGO_UNDERGROUND_MIX}
            alt=""
            aria-hidden="true"
            className="brand-logo h-14 w-14 sm:h-16 sm:w-16 object-contain"
          />
          <span
            className="hidden md:inline-block text-[10px] uppercase"
            style={{
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "0.32em",
              color: "#f0ebd8",
            }}
          >
            Premium One Stop
          </span>
        </a>

        {/* Desktop menu */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => {
            const isLiveChat = item.href === "#live-chat";
            return (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => handleClick(e, item.href)}
                className="group relative text-[10px] uppercase transition-colors duration-300"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.32em",
                  color: isLiveChat ? "#1972F5" : "#a3a198",
                  fontWeight: isLiveChat ? 600 : 400,
                  textDecoration: "none",
                }}
              >
                <span className="hover-target">{item.label}</span>
                <span
                  className="nav-underline"
                  style={isLiveChat ? { background: "#1972F5" } : undefined}
                />
              </a>
            );
          })}
        </nav>

        {/* Mobile toggle */}
        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="nav-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-8 w-8 flex-col items-center justify-center gap-1.5"
        >
          <span
            className="block h-px w-5 transition-transform duration-300"
            style={{
              background: "#f0ebd8",
              transform: open ? "translateY(3px) rotate(45deg)" : "none",
            }}
          />
          <span
            className="block h-px w-5 transition-transform duration-300"
            style={{
              background: "#f0ebd8",
              transform: open ? "translateY(-3px) rotate(-45deg)" : "none",
            }}
          />
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className="md:hidden overflow-hidden transition-[max-height,opacity] duration-500"
        style={{
          maxHeight: open ? "320px" : "0px",
          opacity: open ? 1 : 0,
          borderTop: open ? "1px solid #2a2a26" : "none",
          background: "rgba(5,5,5,0.95)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="flex flex-col px-6 py-6">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={(e) => handleClick(e, item.href)}
              className="border-b py-4 text-[11px] uppercase"
              style={{
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: "0.32em",
                color: "#f0ebd8",
                borderColor: "#2a2a26",
                textDecoration: "none",
              }}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Hero                                                                        */
/* -------------------------------------------------------------------------- */
function TypographicLogo() {
  return (
    <div
      data-testid="premium-one-stop-logo"
      className="relative z-10 flex flex-col items-center select-none"
    >
      <img
        src="/pos-mark.png"
        alt="Premium One Stop seal"
        data-testid="hero-seal"
        className="fade-up relative z-10 mb-5 sm:mb-8 h-32 w-32 sm:h-40 sm:w-40 select-none"
        style={{
          opacity: 0.95,
          filter: "drop-shadow(0 4px 24px rgba(210,86,59,0.18))",
          animationDelay: "0s",
        }}
      />
      <div
        className="fade-up flex items-center gap-3 text-xs sm:text-sm uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.3em",
          color: "#d2563b",
          fontWeight: 600,
          animationDelay: "0.1s",
        }}
      >
        <span className="inline-block h-[2px] w-10 sm:w-14" style={{ background: "#d2563b" }} />
        <span>Est. 2018 — Curated Catalog</span>
        <span className="inline-block h-[2px] w-10 sm:w-14" style={{ background: "#d2563b" }} />
      </div>

      <h1
        className="fade-up mt-6 sm:mt-8 text-center leading-[0.82] tracking-[-0.045em]"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 500,
          fontSize: "clamp(3.5rem, 13vw, 10.5rem)",
          color: "#f0ebd8",
          animationDelay: "0.25s",
        }}
      >
        Premium
      </h1>

      <div
        className="fade-up mt-3 sm:mt-4 flex items-center gap-4"
        style={{ animationDelay: "0.4s" }}
      >
        <span className="inline-block h-px w-16 sm:w-28" style={{ background: "#5e5d58" }} />
        <span className="inline-block h-1.5 w-1.5 rotate-45" style={{ background: "#f0ebd8" }} />
        <span className="inline-block h-px w-16 sm:w-28" style={{ background: "#5e5d58" }} />
      </div>

      <h2
        className="fade-up mt-3 sm:mt-4 text-center leading-[0.82] tracking-[0.01em] italic"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 300,
          fontSize: "clamp(2.5rem, 8.5vw, 6.5rem)",
          color: "#f0ebd8",
          animationDelay: "0.55s",
        }}
      >
        one stop
      </h2>
    </div>
  );
}

function CatalogButton({ label, href, testId, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="fade-up group relative inline-flex items-center justify-center overflow-hidden border transition-all duration-500"
      style={{
        fontFamily: "'Outfit', sans-serif",
        letterSpacing: "0.28em",
        fontSize: "0.78rem",
        fontWeight: 500,
        textTransform: "uppercase",
        padding: "1.15rem 2.4rem",
        borderColor: hovered ? "#8f2b1c" : "#f0ebd8",
        color: "#f0ebd8",
        backgroundColor: hovered ? "#8f2b1c" : "transparent",
        animationDelay: delay,
        textDecoration: "none",
        minWidth: "16rem",
      }}
    >
      <span className="relative z-10 flex items-center gap-4">
        <span className="flex flex-col items-center leading-tight">
          <span>{label}</span>
          <span
            className="mt-1"
            style={{
              fontSize: "0.62rem",
              letterSpacing: "0.42em",
              color: hovered ? "rgba(240,235,216,0.85)" : "#d2563b",
              transition: "color 0.5s ease",
              fontWeight: 600,
            }}
          >
            Catalog
          </span>
        </span>
        <span
          aria-hidden="true"
          className="inline-block transition-transform duration-500"
          style={{ transform: hovered ? "translateX(6px)" : "translateX(0)" }}
        >
          →
        </span>
      </span>
    </a>
  );
}

function Hero() {
  return (
    <section
      id="top"
      data-testid="hero"
      className="relative z-10 flex min-h-[100svh] w-full flex-col items-center justify-center px-6 pb-24 pt-28"
    >
      <TypographicLogo />

      <p
        data-testid="hero-tagline"
        className="fade-up relative z-10 mt-10 max-w-2xl text-center"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontStyle: "italic",
          fontWeight: 300,
          fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)",
          lineHeight: 1.45,
          color: "#a3a198",
          letterSpacing: "0.005em",
          animationDelay: "0.7s",
        }}
      >
        Curated around premium, artist-driven music with real emotional
        identity.{" "}
        <span style={{ color: "#f0ebd8" }}>
          Not built around volume and utility.
        </span>
      </p>

      <div
        data-testid="cta-group"
        className="relative z-10 mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
      >
        <CatalogButton
          label="Premium One Stop"
          href={PREMIUM_ONE_STOP_URL}
          testId="premium-one-stop-btn"
          delay="0.8s"
        />
        <CatalogButton
          label="Easy Clear"
          href={EASY_CLEAR_URL}
          testId="easy-clear-btn"
          delay="0.95s"
        />
      </div>

      {/* Scroll cue */}
      <div
        className="fade-up absolute bottom-24 left-1/2 -translate-x-1/2 text-[9px] uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.4em",
          color: "#5e5d58",
          animationDelay: "1.2s",
        }}
      >
        <span className="scroll-cue">Scroll ↓</span>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable section header                                                     */
/* -------------------------------------------------------------------------- */
function SectionHeader({ overline, title, italicTail }) {
  return (
    <div className="flex flex-col items-start">
      <div
        className="flex items-center gap-3 text-xs sm:text-[13px] uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.3em",
          color: "#d2563b",
          fontWeight: 600,
        }}
      >
        <span className="inline-block h-[2px] w-10" style={{ background: "#d2563b" }} />
        <span>{overline}</span>
      </div>
      <h2
        className="mt-6 leading-[0.92] tracking-[-0.02em]"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 400,
          fontSize: "clamp(2.5rem, 6vw, 4.75rem)",
          color: "#f0ebd8",
        }}
      >
        {title}{" "}
        {italicTail && (
          <span style={{ fontStyle: "italic", fontWeight: 300 }}>{italicTail}</span>
        )}
      </h2>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* About                                                                       */
/* -------------------------------------------------------------------------- */
function About() {
  return (
    <section
      id="about"
      data-testid="section-about"
      className="relative z-10 mx-auto w-full max-w-[1200px] scroll-mt-24 px-6 py-28 md:py-36 md:px-10"
    >
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-5">
          <SectionHeader overline="01 — About" title="A curated" italicTail="destination." />
        </div>
        <div
          className="md:col-span-7"
          style={{
            fontFamily: "'Outfit', sans-serif",
            color: "#a3a198",
            fontSize: "1.05rem",
            lineHeight: 1.75,
          }}
        >
          <p>
            Welcome to Premium One Stop. A curated destination for premium music
            built for sync. Our catalog features both true one-stop songs and
            <span style={{ color: "#f0ebd8" }}> “easy clear”</span> records with
            streamlined approvals and direct relationships that make the
            licensing process fast, efficient, and supervisor-friendly.
          </p>
          <p className="mt-6">
            From cinematic indie and authentic singer-songwriter records to
            modern pop, hip-hop, tension, and trailer-ready moments, every track
            is selected for emotional impact, artistic authenticity, and
            real-world sync usability — without the friction and delays that
            often slow down clearances.
          </p>
          <p
            className="mt-8 pl-6"
            style={{
              borderLeft: "1px solid #8f2b1c",
              color: "#f0ebd8",
              fontStyle: "italic",
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "1.35rem",
              lineHeight: 1.55,
              fontWeight: 300,
            }}
          >
            Unlike traditional production music libraries built around volume
            and utility, Premium One Stop is curated around premium,
            artist-driven music with real emotional identity and cultural
            relevance. Our catalog prioritizes standout songs, direct creator
            relationships, and streamlined clearances — giving clients the
            efficiency of easy-clear music without sacrificing artistry or
            authenticity.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Listen                                                                      */
/* -------------------------------------------------------------------------- */
function PlaylistFrame({ label, src, testId, delay }) {
  return (
    <div
      data-testid={testId}
      className="playlist-frame relative w-full overflow-hidden fade-up"
      style={{
        border: "1px solid #2a2a26",
        background: "#0a0a0a",
        boxShadow: "0 30px 80px -20px rgba(143,43,28,0.18)",
        animationDelay: delay,
      }}
    >
      {/* Oxblood corner brackets */}
      <span aria-hidden="true" className="pointer-events-none absolute left-2 top-2 z-10 h-3 w-3 border-l border-t" style={{ borderColor: "#8f2b1c" }} />
      <span aria-hidden="true" className="pointer-events-none absolute right-2 top-2 z-10 h-3 w-3 border-r border-t" style={{ borderColor: "#8f2b1c" }} />
      <span aria-hidden="true" className="pointer-events-none absolute bottom-2 left-2 z-10 h-3 w-3 border-b border-l" style={{ borderColor: "#8f2b1c" }} />
      <span aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 z-10 h-3 w-3 border-b border-r" style={{ borderColor: "#8f2b1c" }} />

      {/* Label strip */}
      <div
        className="flex items-center justify-between border-b px-4 py-3"
        style={{
          borderColor: "#2a2a26",
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.32em",
          color: "#a3a198",
          fontSize: "10px",
          textTransform: "uppercase",
        }}
      >
        <span className="flex items-center gap-2">
          <span className="inline-block h-1.5 w-1.5 rotate-45" style={{ background: "#8f2b1c" }} />
          {label}
        </span>
        <span style={{ color: "#5e5d58" }}>DISCO.AC</span>
      </div>

      {/* Player */}
      {src ? (
        <iframe
          title={label}
          src={src}
          allowFullScreen
          frameBorder="0"
          className="block w-full"
          style={{ height: "395px", background: "#fff" }}
        />
      ) : (
        <div
          className="flex h-[395px] w-full items-center justify-center px-6 text-center"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            fontSize: "1.15rem",
            color: "#5e5d58",
          }}
        >
          Playlist embed coming soon.
        </div>
      )}
    </div>
  );
}

function Listen() {
  return (
    <section
      id="listen"
      data-testid="section-listen"
      className="relative z-10 mx-auto w-full max-w-[1200px] scroll-mt-24 px-6 py-28 md:py-36 md:px-10"
      style={{ borderTop: "1px solid #2a2a26" }}
    >
      <div className="flex flex-col">
        <div
          className="flex items-center gap-3 text-xs sm:text-[13px] uppercase"
          style={{
            fontFamily: "'Outfit', sans-serif",
            letterSpacing: "0.3em",
            color: "#d2563b",
            fontWeight: 600,
          }}
        >
          <span className="inline-block h-[2px] w-10" style={{ background: "#d2563b" }} />
          <span>02 — Listen</span>
        </div>
        <p
          className="mt-8 max-w-4xl"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontStyle: "italic",
            fontSize: "clamp(1.75rem, 3.4vw, 2.75rem)",
            lineHeight: 1.25,
            letterSpacing: "-0.01em",
            color: "#a3a198",
          }}
        >
          All tracks are{" "}
          <span style={{ color: "#f0ebd8", fontStyle: "normal", fontWeight: 400 }}>
            authentic, artist-driven, NO AI,
          </span>{" "}
          premium songs.{" "}
          <span style={{ color: "#f0ebd8", fontStyle: "normal", fontWeight: 400 }}>
            Press play
          </span>{" "}
          and discover what premium really is.
        </p>
      </div>

      <div className="mt-14 grid gap-8 md:grid-cols-2 md:gap-10">
        <PlaylistFrame
          label="Premium One Stop"
          src={PLAYLIST_PREMIUM_ONE_STOP_SRC}
          testId="playlist-premium-one-stop"
          delay="0.05s"
        />
        <PlaylistFrame
          label="Easy Clear"
          src={PLAYLIST_EASY_CLEAR_SRC}
          testId="playlist-easy-clear"
          delay="0.2s"
        />
      </div>
    </section>
  );
}


/* -------------------------------------------------------------------------- */
/* Reel                                                                        */
/* -------------------------------------------------------------------------- */
function Reel() {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);

  const handlePlay = async () => {
    setStarted(true);
    try {
      await videoRef.current?.play();
    } catch (_) {
      // Autoplay may fail silently in some browsers; user can use native controls.
    }
  };

  return (
    <section
      id="reel"
      data-testid="section-reel"
      className="relative z-10 mx-auto w-full max-w-[1200px] scroll-mt-24 px-6 py-28 md:py-36 md:px-10"
      style={{ borderTop: "1px solid #2a2a26" }}
    >
      <div className="grid gap-12 md:grid-cols-12 md:gap-16 md:items-end">
        <div className="md:col-span-5">
          <SectionHeader
            overline="03 — Reel"
            title="Recent"
            italicTail="work."
          />
        </div>
        <div
          className="md:col-span-7"
          style={{
            fontFamily: "'Outfit', sans-serif",
            color: "#a3a198",
            fontSize: "1.05rem",
            lineHeight: 1.75,
          }}
        >
          <p>
            A taste of what's moving through Premium One Stop right now —
            placements, picture cuts, and records finding their visual home.
          </p>
        </div>
      </div>

      <div
        data-testid="reel-frame"
        className="relative mt-14 overflow-hidden"
        style={{
          border: "1px solid #2a2a26",
          background: "#0a0a0a",
          boxShadow: "0 30px 80px -20px rgba(143,43,28,0.22)",
          aspectRatio: "16 / 9",
        }}
      >
        {/* Frame brackets */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-3 z-20 h-3 w-3 border-l border-t"
          style={{ borderColor: "#8f2b1c" }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-3 z-20 h-3 w-3 border-r border-t"
          style={{ borderColor: "#8f2b1c" }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-3 left-3 z-20 h-3 w-3 border-b border-l"
          style={{ borderColor: "#8f2b1c" }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-3 right-3 z-20 h-3 w-3 border-b border-r"
          style={{ borderColor: "#8f2b1c" }}
        />

        <video
          ref={videoRef}
          data-testid="reel-video"
          src={REEL_URL}
          controls={started}
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full"
          style={{ background: "#000", objectFit: "cover" }}
        >
          Your browser does not support embedded video.{" "}
          <a href={REEL_URL} style={{ color: "#f0ebd8" }}>
            Download the reel
          </a>
          .
        </video>

        {/* Rich closed-state overlay */}
        {!started && (
          <button
            type="button"
            onClick={handlePlay}
            data-testid="reel-play-overlay"
            aria-label="Play reel"
            className="reel-poster group absolute inset-0 z-10 flex h-full w-full cursor-pointer flex-col justify-between p-6 md:p-10 text-left"
            style={{
              background:
                "radial-gradient(ellipse at 50% 55%, rgba(143,43,28,0.32) 0%, rgba(20,8,6,0.65) 38%, rgba(5,5,5,0.92) 78%)",
              border: "none",
              color: "#f0ebd8",
            }}
          >
            {/* Grain layer */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 opacity-[0.22] mix-blend-overlay"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.95 0 0 0 0 0.92 0 0 0 0 0.85 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
              }}
            />

            {/* Scanlines */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 opacity-[0.10]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to bottom, rgba(240,235,216,0.6) 0px, rgba(240,235,216,0.6) 1px, transparent 1px, transparent 3px)",
              }}
            />

            {/* Top row */}
            <div className="relative z-10 flex w-full items-start justify-between">
              <div
                className="flex items-center gap-3 text-[10px] uppercase"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.32em",
                  color: "#f0ebd8",
                }}
              >
                <span className="inline-block h-1.5 w-1.5 rotate-45" style={{ background: "#8f2b1c" }} />
                <span>Reel · 2025</span>
              </div>
              <div
                className="hidden sm:flex items-center gap-3 text-[10px] uppercase"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.32em",
                  color: "#a3a198",
                }}
              >
                <span className="rec-dot" />
                <span>Now Playing — selected placements</span>
              </div>
            </div>

            {/* Center: title + play */}
            <div className="relative z-10 flex flex-1 flex-col items-center justify-center text-center -mt-4">
              <div
                className="text-xs sm:text-[13px] uppercase"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.4em",
                  color: "#d2563b",
                  fontWeight: 600,
                }}
              >
                Featured
              </div>
              <div
                className="mt-3 flex items-center justify-center gap-6 sm:gap-10"
                style={{ lineHeight: 1 }}
              >
                <img
                  src={LOGO_THE_BUS}
                  alt="The Bus Music"
                  data-testid="logo-the-bus-reel"
                  className="reel-brand-logo h-20 w-20 sm:h-28 sm:w-28 object-contain"
                  style={{ filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.45))" }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontWeight: 300,
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    color: "#d2563b",
                    lineHeight: 1,
                  }}
                >
                  &amp;
                </span>
                <img
                  src={LOGO_UNDERGROUND_MIX}
                  alt="Underground Mix"
                  data-testid="logo-underground-mix-reel"
                  className="reel-brand-logo h-20 w-20 sm:h-28 sm:w-28 object-contain"
                  style={{ filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.45))" }}
                />
              </div>
              <div
                className="mt-4 text-[10px] uppercase"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.4em",
                  color: "#a3a198",
                }}
              >
                The Bus Music &nbsp;·&nbsp; Underground Mix
              </div>

              {/* Play button */}
              <div className="relative mt-7 sm:mt-9 flex items-center justify-center">
                <span
                  aria-hidden="true"
                  className="play-pulse absolute h-20 w-20 rounded-full"
                  style={{ border: "1px solid rgba(143,43,28,0.6)" }}
                />
                <span
                  aria-hidden="true"
                  className="play-pulse-2 absolute h-20 w-20 rounded-full"
                  style={{ border: "1px solid rgba(143,43,28,0.6)" }}
                />
                <span
                  className="play-btn relative flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full transition-all duration-500"
                  style={{
                    background: "#8f2b1c",
                    boxShadow:
                      "0 0 0 1px rgba(240,235,216,0.18), 0 20px 40px -8px rgba(143,43,28,0.55)",
                  }}
                >
                  <svg
                    width="22"
                    height="26"
                    viewBox="0 0 22 26"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M21 11.2679C22.3333 12.0377 22.3333 13.9623 21 14.7321L4.5 24.2583C3.16667 25.0281 1.5 24.0658 1.5 22.5263L1.5 3.47372C1.5 1.93418 3.16667 0.97189 4.5 1.7417L21 11.2679Z"
                      fill="#f0ebd8"
                      stroke="#f0ebd8"
                    />
                  </svg>
                </span>
              </div>

              <div
                className="mt-6 text-[10px] uppercase"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.4em",
                  color: "#a3a198",
                }}
              >
                Tap to play
              </div>
            </div>

            {/* Bottom row */}
            <div className="relative z-10 flex w-full items-end justify-between">
              <div
                className="text-[10px] uppercase"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.32em",
                  color: "#5e5d58",
                }}
              >
                Premium One Stop
              </div>
              <div
                className="hidden sm:block text-right text-[10px] uppercase"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  letterSpacing: "0.32em",
                  color: "#5e5d58",
                }}
              >
                Sync · Licensing · Catalog
              </div>
            </div>
          </button>
        )}
      </div>

      <div
        className="mt-6 flex items-center justify-between text-[10px] uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.32em",
          color: "#5e5d58",
        }}
      >
        <span>The Bus &amp; Underground Mix — Reel</span>
        <span>Premium One Stop · Underground Mix</span>
      </div>
    </section>
  );
}


/* -------------------------------------------------------------------------- */
/* How It Works                                                                */
/* -------------------------------------------------------------------------- */
const STEPS = [
  {
    n: "I.",
    title: "Browse the catalog",
    body:
      "Stream and download our one-stop and easy-clear records directly from DISCO.AC. Filter by mood, tempo, genre, or vocal.",
  },
  {
    n: "II.",
    title: "Request a quote",
    body:
      "Send the track, the use, the term, and the territory. We respond fast — usually within 24 hours, often the same day.",
  },
  {
    n: "III.",
    title: "Clear it without friction",
    body:
      "True one-stops are cleared on the spot. Easy-clears come with direct relationships to the writers and masters — no chasing splits.",
  },
  {
    n: "IV.",
    title: "License and deliver",
    body:
      "Sign, pay, and receive your fully-cleared assets — stems, instrumentals, and alt versions when available.",
  },
];

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="section-how-it-works"
      className="relative z-10 mx-auto w-full max-w-[1200px] scroll-mt-24 px-6 py-28 md:py-36 md:px-10"
      style={{ borderTop: "1px solid #2a2a26" }}
    >
      <SectionHeader
        overline="04 — How It Works"
        title="Sync without"
        italicTail="the friction."
      />

      <div className="mt-16 grid gap-10 md:grid-cols-2 md:gap-x-20 md:gap-y-14">
        {STEPS.map((s) => (
          <div key={s.n} data-testid={`step-${s.n.replace(".", "")}`} className="flex flex-col">
            <div
              className="flex items-baseline gap-4"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "#d2563b",
                fontStyle: "italic",
                fontSize: "1.5rem",
              }}
            >
              <span>{s.n}</span>
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: "normal",
                  fontSize: "1.6rem",
                  color: "#f0ebd8",
                  lineHeight: 1.1,
                }}
              >
                {s.title}
              </span>
            </div>
            <p
              className="mt-4"
              style={{
                fontFamily: "'Outfit', sans-serif",
                color: "#a3a198",
                fontSize: "1rem",
                lineHeight: 1.7,
              }}
            >
              {s.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Pricing                                                                     */
/* -------------------------------------------------------------------------- */
const TIERS = [
  {
    name: "Indie",
    scope: "Short films · Indie features · Doc · Podcast · Web",
    note: "Friendly rates for independent storytellers.",
  },
  {
    name: "Broadcast",
    scope: "TV episodics · Streaming series · Network promo · Trailers",
    note: "Quoted by usage, term, and territory.",
  },
  {
    name: "Brand & Theatrical",
    scope: "Global ad campaigns · Theatrical features · AAA games",
    note: "Tier-one pricing for tier-one productions.",
  },
];

function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="section-pricing"
      className="relative z-10 mx-auto w-full max-w-[1200px] scroll-mt-24 px-6 py-28 md:py-36 md:px-10"
      style={{ borderTop: "1px solid #2a2a26" }}
    >
      <SectionHeader
        overline="05 — Pricing"
        title="Fair, fast,"
        italicTail="and bespoke."
      />

      <p
        className="mt-8 max-w-3xl"
        style={{
          fontFamily: "'Outfit', sans-serif",
          color: "#a3a198",
          fontSize: "1.05rem",
          lineHeight: 1.75,
        }}
      >
        Every project is unique. We quote on a deal-by-deal basis to fit the
        use, scope, and term — and we turn quotes around in hours, not weeks.
      </p>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {TIERS.map((t, i) => (
          <div
            key={t.name}
            data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex flex-col p-8 transition-colors duration-300"
            style={{
              border: "1px solid #2a2a26",
              background: "rgba(15,15,13,0.55)",
            }}
          >
            <div
              className="text-[10px] uppercase"
              style={{
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: "0.32em",
                color: "#5e5d58",
              }}
            >
              {String(i + 1).padStart(2, "0")} · Tier
            </div>
            <h3
              className="mt-4"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "2rem",
                fontWeight: 400,
                color: "#f0ebd8",
                lineHeight: 1.1,
              }}
            >
              {t.name}
            </h3>
            <p
              className="mt-4"
              style={{
                fontFamily: "'Outfit', sans-serif",
                color: "#a3a198",
                fontSize: "0.9rem",
                lineHeight: 1.6,
              }}
            >
              {t.scope}
            </p>
            <p
              className="mt-6 pt-6"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: "italic",
                color: "#d2563b",
                fontSize: "1.05rem",
                borderTop: "1px solid #2a2a26",
              }}
            >
              {t.note}
            </p>
          </div>
        ))}
      </div>

      <p
        className="mt-10 text-[11px] uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.32em",
          color: "#5e5d58",
        }}
      >
        Custom quotes within 24 hours · Worldwide rights available
      </p>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Live Chat                                                                   */
/* -------------------------------------------------------------------------- */
function LiveChat() {
  const [hovered, setHovered] = useState(false);

  const openCrisp = () => {
    // Open the Chatlio chat widget when available.
    try {
      // eslint-disable-next-line no-undef
      window._chatlio?.open();
    } catch (_) {}
  };

  return (
    <section
      id="live-chat"
      data-testid="section-live-chat"
      className="relative z-10 mx-auto w-full max-w-[1200px] scroll-mt-24 px-6 py-28 md:py-36 md:px-10"
      style={{ borderTop: "1px solid #2a2a26" }}
    >
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-5">
          <SectionHeader
            overline="06 — Live Chat"
            title="Chat with a human"
            italicTail="catalog concierge."
          />
        </div>
        <div className="md:col-span-7">
          {/* Status pill */}
          <div
            className="inline-flex items-center gap-3 px-4 py-2"
            style={{
              border: "1px solid #2a2a26",
              background: "rgba(15,15,13,0.55)",
            }}
          >
            <span className="rec-dot" />
            <span
              className="text-[10px] uppercase"
              style={{
                fontFamily: "'Outfit', sans-serif",
                letterSpacing: "0.32em",
                color: "#a3a198",
              }}
            >
              Live · Real humans, no AI · North America business hours
            </span>
          </div>

          <p
            className="mt-8 max-w-2xl"
            style={{
              fontFamily: "'Outfit', sans-serif",
              color: "#a3a198",
              fontSize: "1.05rem",
              lineHeight: 1.75,
            }}
          >
            Need music fast? Our team can help you search the catalog in real
            time, confirm clearance status, and send curated options based on
            your{" "}
            <span style={{ color: "#f0ebd8" }}>scene, brand, deadline,</span>{" "}
            and usage needs.
          </p>

          <button
            type="button"
            data-testid="open-live-chat-btn"
            onClick={openCrisp}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="mt-10 inline-flex items-center justify-center gap-4 border transition-all duration-500"
            style={{
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "0.28em",
              fontSize: "0.78rem",
              fontWeight: 500,
              textTransform: "uppercase",
              padding: "1.15rem 2.4rem",
              borderColor: hovered ? "#8f2b1c" : "#f0ebd8",
              color: "#f0ebd8",
              backgroundColor: hovered ? "#8f2b1c" : "transparent",
              cursor: "pointer",
            }}
          >
            <span>Open Live Chat</span>
            <span
              aria-hidden="true"
              className="inline-block transition-transform duration-500"
              style={{ transform: hovered ? "translateX(6px)" : "translateX(0)" }}
            >
              →
            </span>
          </button>

          <p
            className="mt-6 text-[10px] uppercase"
            style={{
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "0.32em",
              color: "#5e5d58",
            }}
          >
            Off-hours? Use Contact below — replies within one business day.
          </p>
        </div>
      </div>
    </section>
  );
}


/* -------------------------------------------------------------------------- */
/* Contact                                                                     */
/* -------------------------------------------------------------------------- */
function Contact() {
  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="relative z-10 mx-auto w-full max-w-[1200px] scroll-mt-24 px-6 py-28 md:py-36 md:px-10"
      style={{ borderTop: "1px solid #2a2a26" }}
    >
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-5">
          <SectionHeader
            overline="07 — Contact"
            title="Let's"
            italicTail="talk."
          />
        </div>
        <div className="md:col-span-7 flex flex-col">
          <div
            className="text-[10px] uppercase"
            style={{
              fontFamily: "'Outfit', sans-serif",
              letterSpacing: "0.32em",
              color: "#5e5d58",
            }}
          >
            Sync · Licensing · Catalog Inquiries
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(2rem, 4vw, 3.25rem)",
                fontWeight: 400,
                color: "#f0ebd8",
                lineHeight: 1.1,
              }}
            >
              Devon DeVries
            </span>
            <a
              href="mailto:info@underground-mix.com"
              data-testid="contact-email"
              className="email-link group inline-flex items-center gap-3"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: "italic",
                fontSize: "clamp(1.4rem, 2.4vw, 2rem)",
                fontWeight: 400,
                color: "#a3a198",
                textDecoration: "none",
                width: "fit-content",
              }}
            >
              <span className="email-underline">info@underground-mix.com</span>
              <span
                aria-hidden="true"
                className="transition-transform duration-500 group-hover:translate-x-1"
              >
                →
              </span>
            </a>
          </div>

          <p
            className="mt-10 max-w-xl"
            style={{
              fontFamily: "'Outfit', sans-serif",
              color: "#a3a198",
              fontSize: "1rem",
              lineHeight: 1.75,
            }}
          >
            For info on how to have your songs and artistry inside of Premium
            One Stop, visit{" "}
            <a
              href="https://www.underground-mix.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-underground-link"
              className="inline-link"
              style={{
                color: "#f0ebd8",
                textDecoration: "none",
                borderBottom: "1px solid #8f2b1c",
                paddingBottom: "1px",
              }}
            >
              underground-mix.com
            </a>
            . The one-stop content is driven by a{" "}
            <span style={{ color: "#f0ebd8", fontStyle: "italic" }}>
              premium creative community
            </span>
            .
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Footer + Marquee                                                            */
/* -------------------------------------------------------------------------- */
function Marquee() {
  const phrase = "PREMIUM ONE STOP";
  const sep = " ✦ ";
  const tail = "A&R MUSIC CATALOG";
  const items = Array.from({ length: 8 }).map(
    () => `${phrase}${sep}${tail}${sep}EASY CLEAR${sep}`
  );

  return (
    <div
      data-testid="editorial-marquee"
      className="pointer-events-none relative z-[5] overflow-hidden border-t border-b"
      style={{
        borderColor: "#2a2a26",
        background: "rgba(5,5,5,0.65)",
      }}
    >
      <div
        className="marquee-track whitespace-nowrap py-4 text-[11px]"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.45em",
          color: "#a3a198",
          textTransform: "uppercase",
        }}
      >
        {items.join("")}
        {items.join("")}
      </div>
    </div>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-col items-start justify-between gap-6 px-6 py-12 md:flex-row md:items-center md:px-10"
    >
      <div
        className="flex items-center gap-3 text-[10px] uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.32em",
          color: "#5e5d58",
        }}
      >
        <img
          src="/pos-mark.png"
          alt=""
          aria-hidden="true"
          className="h-7 w-7 select-none"
          style={{ opacity: 0.7 }}
        />
        <span>© {year} — Premium One Stop</span>
      </div>

      <div className="flex items-center gap-6">
        <div
          className="hidden sm:block text-[10px] uppercase"
          style={{
            fontFamily: "'Outfit', sans-serif",
            letterSpacing: "0.32em",
            color: "#5e5d58",
          }}
        >
          In partnership with
        </div>
        <a
          href="https://www.underground-mix.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-the-bus"
          className="footer-logo-link"
          aria-label="The Bus Music"
        >
          <img
            src={LOGO_THE_BUS}
            alt="The Bus Music"
            className="h-10 w-10 object-contain"
            style={{ opacity: 0.65 }}
          />
        </a>
        <span
          aria-hidden="true"
          className="h-px w-6"
          style={{ background: "#2a2a26" }}
        />
        <a
          href="https://www.underground-mix.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-underground-mix"
          className="footer-logo-link"
          aria-label="Underground Mix"
        >
          <img
            src={LOGO_UNDERGROUND_MIX}
            alt="Underground Mix"
            className="h-10 w-10 object-contain"
            style={{ opacity: 0.65 }}
          />
        </a>
      </div>

      <div
        className="text-[10px] uppercase"
        style={{
          fontFamily: "'Outfit', sans-serif",
          letterSpacing: "0.32em",
          color: "#5e5d58",
        }}
      >
        A Premium Creative Community
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/* App                                                                         */
/* -------------------------------------------------------------------------- */
const Home = () => {
  useEffect(() => {
    document.title = "Premium One Stop — Curated Music Catalog";
  }, []);

  return (
    <main
      data-testid="landing-root"
      className="relative w-full overflow-x-hidden"
      style={{ background: "#050505" }}
    >
      <BackgroundNoise />
      <Nav />
      <Hero />
      <About />
      <Listen />
      <Reel />
      <HowItWorks />
      <Pricing />
      <LiveChat />
      <Contact />
      <Marquee />
      <Footer />
    </main>
  );
};

function App() {
  return <Home />;
}

export default App;
